import {siteMapApi} from "@localization/siteMap/siteMap";
import {IApiEndpoint, IOperationResponse} from "@localization/siteMap/siteMapInterfaces.ts";
import {PropsWithChildren, useCallback, useEffect, useRef} from "react";
import {ApiContext} from "@contexts/hooks/api/useApi.ts";
import lodash from 'lodash'

function monitorPromise<T>(promise: Promise<T>) {
    let status: 'pending' | 'fulfilled' | 'rejected' = 'pending';
    const wrapped = promise.then(
        value => {
            status = 'fulfilled';
            return value
        },
        err => {
            status = 'rejected';
            throw err
        }
    );
    // @ts-ignore
    wrapped.status = () => status;
    // @ts-ignore
    wrapped.timestamp = Date.now();
    return wrapped;
}

/**
 * @see useApi
 * @see useApiHealthStatus
 * @see useApiPromises
 * @param {ReactNode} children
 * @constructor
 */
export function ApiProvider({children}: PropsWithChildren) {
    // 1. Substituímos useState por useRef para evitar re-renders
    const promisesRef = useRef<(Promise<Response> & { status?: any, timestamp?: number })[]>([]);
    const lastRequestsRef = useRef<(IApiEndpoint & { time: number })[]>([]);

    // 2. Ref para armazenar os "ouvintes" (listeners) de mudanças nas promises
    const promiseListenersRef = useRef<Set<() => void>>(new Set());

    // 3. Função para notificar todos os ouvintes
    const notifyPromiseListeners = useCallback(() => {
        promiseListenersRef.current.forEach(callback => callback());
    }, []);

    // Limpa promises e requisições antigas
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            let promisesChanged = false;

            // Remove promises concluídas ou rejeitadas
            const initialPromiseCount = promisesRef.current.length;
            promisesRef.current = promisesRef.current.filter(p => p.status() === 'pending' || (Date.now() - (p.timestamp || 0) < 30000));
            if (promisesRef.current.length !== initialPromiseCount) {
                promisesChanged = true;
            }

            // Remove requisições antigas (mais de 5 segundos)
            lastRequestsRef.current = lastRequestsRef.current.filter(request => (Date.now() - request.time) < 5000);

            // Notifica os listeners APENAS se a lista de promises mudou
            if (promisesChanged) {
                notifyPromiseListeners();
            }

        }, 3000);

        return () => clearInterval(cleanupInterval);
    }, [notifyPromiseListeners]);

    const GetFile = async (endpoint: IApiEndpoint<any>, ignoreCache: boolean = false) => {
        // ... (sua função GetFile permanece igual)
        const url = siteMapApi.buildEndpoint(endpoint);
        const options = siteMapApi.buildFetchOptions(endpoint);
        const response = await fetch(url, {...options, cache: ignoreCache ? 'no-cache' : 'default'});

        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        return response.blob();
    }

    async function Get(endpoint: IApiEndpoint<any>,
                       /**
                        * @deprecated use endpoint.options?.ignoreCache
                        */
                       ignoreCache?: boolean,
                       /**
                        * @deprecated use endpoint.options?.ignoreLoading
                        */
                       ignoreLoading?: boolean) {
        if (endpoint.method !== 'GET') {
            const now = new Date().getTime();
            // 4. Lendo e escrevendo diretamente no .current do ref
            const similarRequest = lastRequestsRef.current.find(req => {
                const isSameEndpoint = lodash.isMatch({
                    url: req.url,
                    body: req.body,
                    data: req.data,
                    method: req.method
                }, {url: endpoint.url, body: endpoint.body, data: endpoint.data, method: endpoint.method});
                const isRecent = (now - req.time) < 1500;
                return isSameEndpoint && isRecent;
            });

            if (similarRequest) {
                return {} as IOperationResponse<typeof endpoint.type>;
            }

            lastRequestsRef.current.push({...endpoint, time: now});
        }

        const newPromise = GetSync(endpoint, endpoint.options?.ignoreCache ?? ignoreCache, endpoint.options?.abortSignal);

        const shouldIgnoreLoading = endpoint.options?.ignoreLoading != null ? endpoint.options.ignoreLoading : ignoreLoading;

        if (!shouldIgnoreLoading) {
            const monitored = monitorPromise(newPromise);
            // 5. Adicionando a promise ao ref e notificando
            promisesRef.current.push(monitored);
            notifyPromiseListeners();
        }

        return await newPromise.then(x => {
            return x.json() as unknown as IOperationResponse<typeof endpoint.type>
        }).finally(() => {
            if (!shouldIgnoreLoading) {
                promisesRef.current = promisesRef.current.filter(p => p !== newPromise);
                notifyPromiseListeners();
            }
        }).catch((_) => {
            const errmsg: IOperationResponse = {
                hasData: false,
                message: "Ocorreu um erro ao processar sua solicitação",
                status: 'Error',
                items: undefined,
                pagination: undefined,
                responseType: typeof endpoint.type,
                ultilReferences: {}
            }
            return errmsg
        }) as IOperationResponse<typeof endpoint.type>;
    }

    const GetSync = (endpoint: IApiEndpoint<any>, ignoreCache?: boolean, abortSignal?: AbortSignal) => {
        const url = siteMapApi.buildEndpoint(endpoint);
        const options = siteMapApi.buildFetchOptions(endpoint);
        const noCacheFromPagination = endpoint.pagination?.ignoreCache ?? false;

        return fetch(url, {
            ...options,
            cache: (noCacheFromPagination || ignoreCache) ? 'no-cache' : 'default',
            signal: abortSignal
        });
    };

    // 6. Funções para o sistema de assinatura
    const getPromises = useCallback(() => promisesRef.current, []);

    const subscribeToPromises = useCallback((callback: () => void) => {
        promiseListenersRef.current.add(callback);
        // Retorna uma função de "unsubscribe" para limpeza
        return () => {
            promiseListenersRef.current.delete(callback);
        };
    }, []);

    const contextValue = {
        Get,
        GetSync,
        GetFile,
        getPromises,
        subscribeToPromises
    };

    return (
        <ApiContext.Provider value={contextValue}>
            {children}
        </ApiContext.Provider>
    );
}

